import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Modal } from 'react-bootstrap';
import { auth, db, functions } from '../../firebase.ts';
import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { MdDeleteForever } from "react-icons/md";
import Icon from '../UI/Icon';
import AusspracheTestModal from '../Dashboard/AusspracheTestModal';
import { getProblemSounds, getChildAgeFromTest, getInProgressTest, deleteTest } from '../../services/pronunciationTestService';

function Settings({ onNavigate, onSettingsSaved }) {
  // TODO: Changes we need to tackle after merge conflict
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [familyData, setFamilyData] = useState({
    familyName: '',
    // parentEmail: '', // Removed as per requirement
    childAge: ''
  });
  const [originalFamilyData, setOriginalFamilyData] = useState({
    familyName: '',
    // parentEmail: '', // Removed as per requirement
    childAge: ''
  });
  const [originalSelectedCharacters, setOriginalSelectedCharacters] = useState([]);
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [originalNotifications, setOriginalNotifications] = useState(true);

  const [loading, setLoading] = useState(true);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: ''
  });
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [userId, setUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Personal information fields
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('family');
  const [language, setLanguage] = useState('de');
  const [notifications, setNotifications] = useState(true);
  
  // Password change fields
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Account deletion fields
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // Subscription cancellation fields
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');
  
  const [severeProblemSounds, setSevereProblemSounds] = useState([]);
  const [mildProblemSounds, setMildProblemSounds] = useState([]);
  const [showAusspracheTest, setShowAusspracheTest] = useState(false);
  const [showRepeatTestModal, setShowRepeatTestModal] = useState(false);
  const [inProgressTestData, setInProgressTestData] = useState(null);
  const [pendingFreshTest, setPendingFreshTest] = useState(false); // Flag to open fresh test after state clears

  const [currentPlan, setCurrentPlan] = useState({
    name: 'Kein Plan',
    status: 'Inaktiv',
    expiryDate: null,
    features: []
  });

  const [userSubscription, setUserSubscription] = useState(null);

  // Check if user has monthly_promo plan (games-only access)
  const hasGamesOnlyAccess = () => {
    if (!userSubscription) return false;
    return userSubscription.planKey === 'monthly_promo' ||
           userSubscription.type === 'monthly_promo';
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No user logged in');
        return;
      }

      setUserId(user.uid);
      setEmail(user.email || '');
      
      // Check if user signed in with Google
      const googleProvider = user.providerData.find(
        provider => provider.providerId === 'google.com'
      );
      setIsGoogleUser(!!googleProvider);
      
      // Load user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Set personal information
        const loadedDisplayName = userData.displayName || '';
        const loadedNotifications = userData.settings?.notifications ?? true;
        setDisplayName(loadedDisplayName);
        setOriginalDisplayName(loadedDisplayName);
        setRole(userData.role || 'family');
        setLanguage(userData.settings?.language || 'de');
        setNotifications(loadedNotifications);
        setOriginalNotifications(loadedNotifications);
        
        // Set family data
        const loadedFamilyData = {
          familyName: userData.familyName || userData.displayName || '',
          // parentEmail: userData.parentEmail || user.email || '', // Removed as per requirement
          childAge: userData.childAge || ''
        };
        setFamilyData(loadedFamilyData);
        setOriginalFamilyData(loadedFamilyData);
        
        // Set selected characters
        if (userData.selectedCharacters && Array.isArray(userData.selectedCharacters)) {
          setSelectedCharacters(userData.selectedCharacters);
          setOriginalSelectedCharacters(userData.selectedCharacters);
        }

        // Load problem sounds from pronunciation tests collection
        const problemSoundsData = await getProblemSounds(user.uid);
        setSevereProblemSounds(problemSoundsData.severeProblemSounds || []);
        setMildProblemSounds(problemSoundsData.mildProblemSounds || []);

        // Check for in-progress test
        const inProgressTest = await getInProgressTest(user.uid);
        if (inProgressTest) {
          setInProgressTestData(inProgressTest);
        }

        // Load child age from pronunciation tests collection
        const childAgeData = await getChildAgeFromTest(user.uid);

        if (childAgeData) {
          // Format child age as "X Jahre, Y Monate"
          const formattedAge = `${childAgeData.years} ${childAgeData.years === 1 ? 'Jahr' : 'Jahre'}, ${childAgeData.months} ${childAgeData.months === 1 ? 'Monat' : 'Monate'}`;
          setFamilyData(prev => ({
            ...prev,
            childAge: formattedAge
          }));
          setOriginalFamilyData(prev => ({
            ...prev,
            childAge: formattedAge
          }));
        }

        // Set subscription data if available
        if (userData.subscription && userData.subscription.isActive) {
          const subscription = userData.subscription;
          setUserSubscription(subscription); // Store subscription for access control
          let expiryDate;
          
          // Check if there's already an end date or expiry date
          if (subscription.endDate) {
            expiryDate = subscription.endDate.toDate ? subscription.endDate.toDate() : new Date(subscription.endDate);
          } else if (subscription.expiryDate) {
            expiryDate = subscription.expiryDate.toDate ? subscription.expiryDate.toDate() : new Date(subscription.expiryDate);
          } else {
            // Calculate expiry date based on start date and billing period
            const startDate = subscription.startDate?.toDate ? subscription.startDate.toDate() : 
                           (subscription.startDate ? new Date(subscription.startDate) : new Date());
            
            expiryDate = new Date(startDate);
            
            // Calculate based on billing period or plan key
            if (subscription.billingPeriod === 'pass_6m' || subscription.planKey === 'pass_6m' || subscription.type === 'pass_6m' || subscription.name?.includes('Paket 2')) {
              // 6-month pass - fixed duration, doesn't renew
              expiryDate.setMonth(expiryDate.getMonth() + 6);
            } else if (subscription.billingPeriod === 'monthly' || subscription.planKey === 'monthly') {
              expiryDate.setMonth(expiryDate.getMonth() + 1);
            } else if (subscription.billingPeriod === 'quarterly') {
              expiryDate.setMonth(expiryDate.getMonth() + 3);
            } else if (subscription.billingPeriod === 'semiannual') {
              expiryDate.setMonth(expiryDate.getMonth() + 6);
            } else {
              // Default to monthly if no billing period specified
              expiryDate.setMonth(expiryDate.getMonth() + 1);
            }
            
            // Check if we're past the calculated expiry date and need to add another period
            const now = new Date();
            while (expiryDate < now) {
              if (subscription.billingPeriod === 'pass_6m' || subscription.planKey === 'pass_6m' || subscription.type === 'pass_6m' || subscription.name?.includes('Paket 2')) {
                // 6-month pass doesn't renew - it's a one-time purchase
                break;
              } else if (subscription.billingPeriod === 'monthly' || subscription.planKey === 'monthly') {
                expiryDate.setMonth(expiryDate.getMonth() + 1);
              } else if (subscription.billingPeriod === 'quarterly') {
                expiryDate.setMonth(expiryDate.getMonth() + 3);
              } else if (subscription.billingPeriod === 'semiannual') {
                expiryDate.setMonth(expiryDate.getMonth() + 6);
              } else {
                expiryDate.setMonth(expiryDate.getMonth() + 1);
              }
            }
          }
          
          setCurrentPlan({
            name: subscription.name || 'Basic Plan',
            status: 'Aktiv',
            expiryDate: expiryDate ? expiryDate.toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit', 
              year: 'numeric'
            }) : null,
            features: subscription.features || []
          });
        } else {
          // No active subscription
          setCurrentPlan({
            name: 'Kein Plan',
            status: 'Inaktiv',
            expiryDate: null,
            features: []
          });
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCharacter = (char) => {
    setSelectedCharacters(prev => 
      prev.includes(char) 
        ? prev.filter(c => c !== char)
        : [...prev, char]
    );
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    if (!userId) {
      console.error('No user ID available');
      return;
    }

    setSaving(true);

    try {
      const user = auth.currentUser;
      
      // Update display name in Auth if it changed
      if (user && displayName.trim() !== user.displayName) {
        await updateProfile(user, {
          displayName: displayName.trim()
        });
      }
      
      // Save to Firestore
      await updateDoc(doc(db, 'users', userId), {
        displayName: displayName.trim(),
        // role: role, // Removed as per requirement
        settings: {
          // language: language, // Removed as per requirement
          notifications: notifications
        },
        selectedCharacters: selectedCharacters,
        // Save family data
        familyName: familyData.familyName,
        // parentEmail: familyData.parentEmail, // Removed as per requirement
        // childAge is now read-only from pronunciation test, not saved here
        updatedAt: new Date()
      });

      // Update original data to reflect saved state
      setOriginalFamilyData(familyData);
      setOriginalSelectedCharacters(selectedCharacters);
      setOriginalDisplayName(displayName.trim());
      setOriginalNotifications(notifications);
      setHasChanges(false);
      
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
      
      // Notify parent component that settings were saved
      if (onSettingsSaved) {
        onSettingsSaved();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      // Optionally show error alert
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate password fields
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError('Bitte fülle alle Passwortfelder aus');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setPasswordLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Kein Benutzer angemeldet');

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setPasswordSuccess('Passwort erfolgreich geändert');
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Hide password form after successful change
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Password update error:', error);
      
      switch (error.code) {
        case 'auth/wrong-password':
          setPasswordError('Das aktuelle Passwort ist falsch');
          break;
        case 'auth/weak-password':
          setPasswordError('Das neue Passwort ist zu schwach');
          break;
        default:
          setPasswordError('Fehler beim Ändern des Passworts');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFamilyData(originalFamilyData);
    setSelectedCharacters(originalSelectedCharacters);
    setDisplayName(originalDisplayName);
    setNotifications(originalNotifications);
    setIsEditMode(false);
    setHasChanges(false);
  };

  const handleToggleEditMode = () => {
    if (isEditMode && hasChanges) {
      handleSaveSettings();
    } else if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
    }
  };

  const handleContactSubmit = () => {
    // In real app, this would send the message
    setShowContactModal(false);
    setContactForm({ subject: '', message: '' });
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      setFeedbackError('Bitte gib dein Feedback ein');
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        setFeedbackError('Du musst angemeldet sein, um Feedback zu senden');
        return;
      }

      // Save feedback to Firebase
      await addDoc(collection(db, 'feedback'), {
        userId: user.uid,
        userEmail: user.email,
        userName: displayName || user.displayName || 'Anonymous',
        feedback: feedback.trim(),
        createdAt: new Date(),
        status: 'new' // Can be used for tracking if feedback has been reviewed
      });

      // Clear feedback and show success
      setFeedback('');
      setFeedbackSuccess(true);
      setTimeout(() => setFeedbackSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackError('Fehler beim Senden des Feedbacks. Bitte versuche es später erneut.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // The app will redirect to login page automatically due to auth state change
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    
    // Validate password for email/password users
    if (!isGoogleUser && !deletePassword) {
      setDeleteError('Bitte gib dein Passwort ein, um dein Konto zu löschen');
      return;
    }

    setDeleteLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Kein Benutzer angemeldet');

      // Re-authenticate user if they use email/password
      if (!isGoogleUser) {
        const credential = EmailAuthProvider.credential(user.email, deletePassword);
        await reauthenticateWithCredential(user, credential);
      }

      // Call the cloud function to delete the user
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');
      const result = await deleteUserFunction();
      
      // Show success message
      setDeleteSuccess(true);
      
      // Close the modal and redirect after a short delay
      setTimeout(() => {
        setShowDeleteConfirmModal(false);
        // Redirect to home/login page after successful deletion
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Account deletion error:', error);
      
      // Handle different types of errors
      if (error.code === 'auth/wrong-password') {
        setDeleteError('Das Passwort ist falsch');
      } else if (error.code === 'auth/requires-recent-login') {
        setDeleteError('Bitte melde dich erneut an und versuche es dann nochmal');
      } else if (error.code === 'functions/unauthenticated') {
        setDeleteError('Du musst angemeldet sein, um dein Konto zu löschen');
      } else if (error.code === 'functions/internal') {
        setDeleteError('Serverfehler beim Löschen des Kontos. Bitte versuche es später nochmal.');
      } else {
        setDeleteError('Fehler beim Löschen des Kontos: ' + (error.message || 'Unbekannter Fehler'));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError('');

    try {
      const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelSubscriptionFn();

      // Close modal and reload user data
      setShowCancelModal(false);
      await loadUserData();

      // Show success modal
      setCancelSuccessMessage(result.data.message);
      setShowCancelSuccessModal(true);
    } catch (error) {
      console.error('Cancellation error:', error);
      setCancelError(error.message || 'Fehler beim Kündigen des Abonnements');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true);

    try {
      const reactivateSubscriptionFn = httpsCallable(functions, 'reactivateSubscription');
      const result = await reactivateSubscriptionFn();

      // Reload user data
      await loadUserData();

      // Show success message
      alert(result.data.message);
    } catch (error) {
      console.error('Reactivation error:', error);
      alert(error.message || 'Fehler beim Reaktivieren des Abonnements');
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleTestComplete = async () => {
    setShowAusspracheTest(false);
    setInProgressTestData(null); // Clear in-progress test
    // Reload user data to get the updated problem sounds
    await loadUserData();
  };

  const handleConfirmRepeatTest = async () => {
    setShowRepeatTestModal(false);

    // Delete any in-progress test from database before starting fresh
    if (inProgressTestData?.id) {
      try {
        await deleteTest(inProgressTestData.id);
      } catch (error) {
        console.error('Error deleting in-progress test:', error);
      }
    }

    setInProgressTestData(null); // Clear local state
    setPendingFreshTest(true); // Signal to open fresh test after state clears
  };

  // Effect to open fresh test modal after inProgressTestData is cleared
  useEffect(() => {
    if (pendingFreshTest && inProgressTestData === null) {
      setShowAusspracheTest(true);
      setPendingFreshTest(false);
    }
  }, [pendingFreshTest, inProgressTestData]);

  if (loading) {
    return (
      <Container>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mb-4 mb-md-5">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
          <div className="flex-grow-1">
            <h1 className="welcome-title text-center text-md-start">Einstellungen</h1>
            <p className="text-secondary text-center text-md-start">
              Verwalte deine Familiendaten, Lerneinstellungen und dein Abonnement
            </p>
          </div>
          <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
            <Button 
              variant={isEditMode && hasChanges ? "success" : isEditMode ? "secondary" : "outline-primary"}
              onClick={handleToggleEditMode}
              size="lg"
              className="w-100 w-sm-auto"
            >
              <Icon name={isEditMode && hasChanges ? "check" : isEditMode ? "close" : "edit"} size="sm" className="me-2" />
              {isEditMode && hasChanges ? "Speichern" : isEditMode ? "Abbrechen" : "Bearbeiten"}
            </Button>
            <Button
              variant="outline-danger"
              onClick={handleSignOut}
              size="lg"
              className="w-100 w-sm-auto logout-btn"
            >
              <Icon name="logout" size="sm" className="me-2" />
              Ausloggen
            </Button>
          </div>
        </div>
      </div>

      {showSuccessAlert && (
        <Alert variant="success" className="mb-4">
          <Icon name="success" size="sm" className="me-2" />
          Einstellungen wurden erfolgreich gespeichert!
        </Alert>
      )}

      <Row className="g-4">
        {/* Family Data Section */}
        <Col lg={6}>
          <Card className="settings-card">
            <Card.Body>
              <div className="settings-header mb-4">
                <div className="settings-icon">
                  <Icon name="profile" size="lg" />
                </div>
                <div>
                  <h4 className="mb-1">Accountdaten</h4>
                  <p className="text-muted mb-0">Verwalte deine persönlichen Informationen</p>
                </div>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={displayName}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      setHasChanges(true);
                    }}
                    className="settings-input"
                    disabled={!isEditMode}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>E-Mail</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    className="settings-input"
                    disabled
                    readOnly
                  />
                  <Form.Text className="text-muted">
                    Die E-Mail-Adresse kann nicht geändert werden
                  </Form.Text>
                </Form.Group>


                <Form.Group className="mb-3">
                  <Form.Label>Alter des Kindes</Form.Label>
                  <Form.Control
                    type="text"
                    value={familyData.childAge}
                    className="settings-input"
                    disabled
                    readOnly
                  />
                  <Form.Text className="text-muted">
                    Das Alter wird aus dem Aussprachetest übernommen
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    label="Benachrichtigungen aktivieren"
                    checked={notifications}
                    onChange={(e) => {
                      setNotifications(e.target.checked);
                      setHasChanges(true);
                    }}
                    disabled={!isEditMode}
                  />
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Current Plan Section */}
        <Col lg={6}>
          <Card className="settings-card plan-card d-flex">
            <Card.Body className="d-flex flex-column">
              <div className="settings-header mb-4">
                <div className="settings-icon plan-icon">
                  <Icon name="trophy" size="lg" />
                </div>
                <div>
                  <h4 className="mb-1">Aktueller Plan</h4>
                  <Badge bg={currentPlan.status === 'Aktiv' ? 'success' : 'secondary'} className="plan-badge">
                    <Icon name={currentPlan.status === 'Aktiv' ? 'check' : 'close'} size="sm" className="me-1" />
                    {currentPlan.status}
                  </Badge>
                </div>
              </div>

              <div className="plan-details mb-4">
                <h5 className="plan-name">{currentPlan.name}</h5>
                {currentPlan.expiryDate && (
                  <p className="plan-expiry text-muted">
                    Gültig bis: <strong>{currentPlan.expiryDate}</strong>
                  </p>
                )}
              </div>

              <div className="flex-grow-1">
                {currentPlan.features.length > 0 ? (
                  <div className="plan-features">
                    <h6 className="mb-3">Enthaltene Leistungen:</h6>
                    <ul className="feature-list">
                      {currentPlan.features.map((feature, index) => (
                        <li key={index} className="feature-item">
                          <Icon name="check" size="sm" className="feature-check" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="no-plan-message text-muted text-center py-3">
                    <p className="mb-0">Wähle einen Plan, um alle Funktionen freizuschalten</p>
                  </div>
                )}
              </div>

              {/* Show cancellation info if subscription is scheduled to cancel */}
              {userSubscription?.cancelAtPeriodEnd && userSubscription?.cancelAt && (
                <Alert variant="warning" className="mb-3">
                  <Icon name="warning" size="sm" className="me-2" />
                  Ihr Abonnement endet am{' '}
                  <strong>
                    {userSubscription.cancelAt.toDate ?
                      userSubscription.cancelAt.toDate().toLocaleDateString('de-DE', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) :
                      new Date(userSubscription.cancelAt).toLocaleDateString('de-DE', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    }
                  </strong>
                </Alert>
              )}

              <div className="d-flex flex-column gap-2 mt-auto">
                <Button
                  variant={currentPlan.status === 'Aktiv' ? 'outline-secondary' : 'primary'}
                  className="w-100 plan-manage-btn"
                  onClick={() => onNavigate('subscription')}
                >
                  {currentPlan.status === 'Aktiv' ? 'Plan ändern' : 'Plan wählen'}
                </Button>

                {/* Cancel or Reactivate button */}
                {currentPlan.status === 'Aktiv' && userSubscription?.stripeSubscriptionId && (
                  <>
                    {userSubscription?.cancelAtPeriodEnd ? (
                      <Button
                        variant="outline-success"
                        className="w-100"
                        onClick={handleReactivateSubscription}
                        disabled={reactivateLoading}
                      >
                        {reactivateLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Wird reaktiviert...
                          </>
                        ) : (
                          <>
                            <Icon name="check" size="sm" className="me-2" />
                            Abonnement reaktivieren
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        className="w-100"
                        onClick={() => setShowCancelModal(true)}
                      >
                        Abonnement kündigen
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Character Selection Section - Hidden for games-only users */}
        {!hasGamesOnlyAccess() && (
          <Col lg={12}>
            <Card className="settings-card">
              <Card.Body>
                <div className="settings-header mb-4">
                  <div className="settings-icon character-icon">
                    <Icon name="edit" size="lg" />
                  </div>
                  <div>
                    <h4 className="mb-1">Zu übende Laute</h4>
                    <p className="text-muted mb-0">
                      Wähle aus den Ziellauten deines Kindes aus, welche geübt werden sollen
                    </p>
                  </div>
                </div>

              <div className="character-selection">
                <div className="mb-4">
                  
                  {(severeProblemSounds.length > 0 || mildProblemSounds.length > 0) ? (
                    <>
                      {/* Show severe problem sounds first */}
                      {severeProblemSounds.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 text-danger">Schwere Ziellaute:</h6>
                          <div className="character-grid">
                            {severeProblemSounds.map(sound => (
                              <Button
                                key={sound}
                                variant={selectedCharacters.includes(sound) ? "primary" : "outline-secondary"}
                                className="character-btn"
                                onClick={() => toggleCharacter(sound)}
                                disabled={!isEditMode}
                              >
                                {sound}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Show mild problem sounds below */}
                      {(() => {
                        // Filter out mild sounds that are already in severe sounds (case-insensitive)
                        const filteredMildSounds = mildProblemSounds.filter(mildSound => 
                          !severeProblemSounds.some(severeSound => 
                            severeSound.toLowerCase() === mildSound.toLowerCase()
                          )
                        );
                        
                        return filteredMildSounds.length > 0 && (
                          <div className="mb-4">
                            <h6 className="mb-2 text-warning">Leichte Ziellaute:</h6>
                            <div className="character-grid">
                              {filteredMildSounds.map(sound => (
                                <Button
                                  key={sound}
                                  variant={selectedCharacters.includes(sound) ? "primary" : "outline-secondary"}
                                  className="character-btn"
                                  onClick={() => toggleCharacter(sound)}
                                  disabled={!isEditMode}
                                >
                                  {sound}
                                </Button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-muted mb-3">
                        <Icon name="info" size="lg" className="mb-2" />
                        <p>Keine Ziellaute gefunden</p>
                        <small>Führe einen Aussprachetest durch, um die Lautschwierigkeiten deines Kindes zu identifizieren</small>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-primary"
                      onClick={async () => {
                        // If there are previous results, show warning before starting new test
                        if (severeProblemSounds.length > 0 || mildProblemSounds.length > 0) {
                          setShowRepeatTestModal(true);
                        }
                        // Otherwise start fresh (but still delete any in-progress test)
                        else {
                          // Delete any in-progress test from database before starting fresh
                          if (inProgressTestData?.id) {
                            try {
                              await deleteTest(inProgressTestData.id);
                            } catch (error) {
                              console.error('Error deleting in-progress test:', error);
                            }
                            setInProgressTestData(null); // Clear any in-progress data
                            setPendingFreshTest(true); // Signal to open fresh test after state clears
                          } else {
                            // No in-progress test, open modal directly
                            setShowAusspracheTest(true);
                          }
                        }
                      }}
                    >
                      {(severeProblemSounds.length > 0 || mildProblemSounds.length > 0) ? 'Test wiederholen' : 'Aussprachetest starten'}
                    </Button>

                    {/* Show "Continue Test" button if there's an in-progress test */}
                    {inProgressTestData && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setShowAusspracheTest(true);
                        }}
                      >
                        Test fortsetzen
                      </Button>
                    )}
                  </div>

                  <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-start">
                      <Icon name="info" size="sm" className="me-2 mt-1 flex-shrink-0 text-muted" />
                      <div>
                        <p className="mb-1 text-muted" style={{ fontSize: '0.9rem' }}>
                          <strong>Hinweis:</strong> Der Aussprachetest kann jederzeit hier im Profil wiederholt werden.
                        </p>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                          Falls du den Test unterbrechen möchtest, wird er automatisch gespeichert und kann hier jederzeit fortgesetzt werden.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        )}

        {/* Password Change Section */}
        {!isGoogleUser && (
          <Col lg={12}>
            <Card className="settings-card">
              <Card.Body>
                <div className="settings-header mb-4">
                  <div className="settings-icon">
                    <Icon name="lock" size="lg" />
                  </div>
                  <div>
                    <h4 className="mb-1">Passwort ändern</h4>
                    <p className="text-muted mb-0">
                      Ändere dein Passwort für mehr Sicherheit
                    </p>
                  </div>
                </div>

                {passwordError && (
                  <Alert variant="danger" className="mb-3">
                    {passwordError}
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert variant="success" className="mb-3">
                    {passwordSuccess}
                  </Alert>
                )}

                {!showPasswordForm ? (
                  <Button 
                    variant="outline-primary" 
                    onClick={() => setShowPasswordForm(true)}
                    disabled={saving}
                  >
                    <Icon name="lock" size="sm" className="me-2" />
                    Passwort ändern
                  </Button>
                ) : (
                  <Form onSubmit={handlePasswordUpdate}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Aktuelles Passwort</Form.Label>
                          <Form.Control
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            disabled={passwordLoading}
                            required
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Neues Passwort</Form.Label>
                          <Form.Control
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={passwordLoading}
                            required
                          />
                          <Form.Text className="text-muted">
                            Mindestens 6 Zeichen
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Neues Passwort bestätigen</Form.Label>
                          <Form.Control
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            disabled={passwordLoading}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <div className="d-flex gap-3">
                      <Button 
                        variant="primary" 
                        type="submit"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Ändert...
                          </>
                        ) : (
                          'Passwort ändern'
                        )}
                      </Button>
                      
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => {
                          setShowPasswordForm(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setPasswordError('');
                          setPasswordSuccess('');
                        }}
                        disabled={passwordLoading}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Google User Info */}
        {isGoogleUser && (
          <Col lg={12}>
            <Card className="settings-card">
              <Card.Body>
                <div className="settings-header mb-4">
                  <div className="settings-icon">
                    <Icon name="lock" size="lg" />
                  </div>
                  <div>
                    <h4 className="mb-1">Anmeldeinformationen</h4>
                    <p className="text-muted mb-0">
                      Dein Konto wird über Google verwaltet
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <svg width="24" height="24" viewBox="0 0 18 18" className="me-3">
                    <g fill="none" fillRule="evenodd">
                      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                    </g>
                  </svg>
                  <div>
                    <p className="mb-0">Du bist mit deinem Google-Konto angemeldet</p>
                    <small className="text-muted">Passwortänderungen müssen über Google vorgenommen werden</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Action Buttons Section */}
        <Col lg={12}>
          <Card className="settings-card actions-card">
            <Card.Body>
              <div className="settings-header mb-4">
                <div className="settings-icon contact-icon">
                  <Icon name="info" size="lg" />
                </div>
                <div>
                  <h4 className="mb-1">Support & Aktionen</h4>
                  <p className="text-muted mb-0">Speichere deine Änderungen oder kontaktiere uns</p>
                </div>
              </div>

              <div className="action-buttons d-flex flex-wrap gap-3">
                <Button
                  variant="outline-primary"
                  size="lg"
                  onClick={() => setShowContactModal(true)}
                >
                  <Icon name="info" size="sm" className="me-2" />
                  Support kontaktieren
                </Button>

                <Button
                  variant="outline-danger"
                  size="lg"
                  onClick={() => setShowDeleteConfirmModal(true)}
                  style={{
                    borderColor: '#dc3545',
                    color: '#dc3545'
                  }}
                >
                  <MdDeleteForever className="me-2" />
                  Konto löschen
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Feedback Section */}
        <Col lg={12}>
          <Card className="settings-card">
            <Card.Body>
              <div className="settings-header mb-4">
                <div className="settings-icon">
                  <Icon name="edit" size="lg" />
                </div>
                <div>
                  <h4 className="mb-1">Feedback</h4>
                  <p className="text-muted mb-0">Teile uns deine Meinung mit und hilf uns, die App zu verbessern</p>
                </div>
              </div>

              {feedbackSuccess && (
                <Alert variant="success" className="mb-3">
                  <Icon name="check" size="sm" className="me-2" />
                  Vielen Dank für dein Feedback! Wir schätzen deine Meinung sehr.
                </Alert>
              )}

              {feedbackError && (
                <Alert variant="danger" className="mb-3">
                  {feedbackError}
                </Alert>
              )}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Dein Feedback</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Was denkst du über unsere App? Hast du Verbesserungsvorschläge oder möchtest du uns etwas mitteilen?"
                    disabled={feedbackSubmitting}
                  />
                  <Form.Text className="text-muted">
                    Dein Feedback wird vertraulich behandelt und hilft uns, die App für alle zu verbessern.
                  </Form.Text>
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={handleFeedbackSubmit}
                  disabled={feedbackSubmitting || !feedback.trim()}
                >
                  {feedbackSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Icon name="arrow-right" size="sm" className="me-2" />
                      Feedback senden
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contact Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Support kontaktieren</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Haben Sie Fragen oder benötigen Hilfe? Wir sind gerne für Sie da!
          </p>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Betreff</Form.Label>
              <Form.Select
                value={contactForm.subject}
                onChange={(e) => setContactForm(prev => ({...prev, subject: e.target.value}))}
              >
                <option value="">Bitte wählen...</option>
                <option value="technical">Technisches Problem</option>
                <option value="billing">Fragen zur Abrechnung</option>
                <option value="content">Fragen zu Inhalten</option>
                <option value="suggestion">Verbesserungsvorschlag</option>
                <option value="other">Sonstiges</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nachricht</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({...prev, message: e.target.value}))}
                placeholder="Beschreiben Sie Ihr Anliegen..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowContactModal(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={handleContactSubmit}
            disabled={!contactForm.subject || !contactForm.message}
          >
            <Icon name="arrow-right" size="sm" className="me-1" />
            Nachricht senden
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Repeat Test Confirmation Modal */}
      <Modal show={showRepeatTestModal} onHide={() => setShowRepeatTestModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
          <Modal.Title></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <h5 className="mb-3">Warnung</h5>
            <p className="text-muted mb-0">
              Wenn Sie den Test wiederholen, werden die aktuellen Testergebnisse überschrieben. 
              Die bisherigen Ziellaute gehen verloren und werden durch die neuen Ergebnisse ersetzt.
            </p>
            <p className="text-muted mt-2 mb-0">
              Möchten Sie den Test wirklich wiederholen?
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="warning" onClick={handleConfirmRepeatTest}>
            Test wiederholen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal 
        show={showDeleteConfirmModal} 
        onHide={() => {
          if (!deleteSuccess) {
            setShowDeleteConfirmModal(false);
            setDeletePassword('');
            setDeleteError('');
            setDeleteSuccess(false);
          }
        }} 
        centered
        size="lg"
      >
        <Modal.Header closeButton={!deleteSuccess} style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }}>
          <Modal.Title className="text-danger">
            <Icon name="warning" size="sm" className="me-2" />
            Konto löschen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="text-danger mb-3">
              <Icon name="warning" size="2xl" />
            </div>
            <h5 className="text-danger mb-3">Achtung: Diese Aktion kann nicht rückgängig gemacht werden!</h5>
            <p className="text-muted mb-3">
              Wenn Sie Ihr Konto löschen, werden alle Ihre Daten unwiderruflich gelöscht, einschließlich:
            </p>
            <ul className="list-unstyled text-muted mb-4">
              <li>• Alle gespeicherten Spielfortschritte</li>
              <li>• Aussprache-Testergebnisse</li>
              <li>• Familiendaten und Einstellungen</li>
              <li>• Aktives Abonnement</li>
            </ul>
          </div>

          {deleteError && (
            <Alert variant="danger" className="mb-3">
              {deleteError}
            </Alert>
          )}

          {deleteSuccess && (
            <Alert variant="success" className="mb-3">
              <Icon name="check" size="sm" className="me-2" />
              Konto wurde erfolgreich gelöscht. Du wirst in Kürze weitergeleitet...
            </Alert>
          )}

          {!isGoogleUser && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Bestätige mit deinem Passwort:
                </Form.Label>
                <Form.Control
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Dein aktuelles Passwort"
                  disabled={deleteLoading || deleteSuccess}
                  required
                />
              </Form.Group>
            </Form>
          )}

          {isGoogleUser && (
            <div className="alert alert-info">
              <Icon name="info" size="sm" className="me-2" />
              Du bist mit Google angemeldet. Klicke auf "Konto löschen", um fortzufahren.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowDeleteConfirmModal(false);
              setDeletePassword('');
              setDeleteError('');
              setDeleteSuccess(false);
            }}
            disabled={deleteLoading || deleteSuccess}
          >
            Abbrechen
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteAccount}
            disabled={deleteLoading || deleteSuccess || (!isGoogleUser && !deletePassword)}
          >
            {deleteLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Wird gelöscht...
              </>
            ) : (
              <>
                <MdDeleteForever className="me-2" />
                Konto endgültig löschen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AusspracheTestModal
        show={showAusspracheTest}
        onClose={handleTestComplete}
        initialState={inProgressTestData}
      />

      {/* Cancel Subscription Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => {
          setShowCancelModal(false);
          setCancelError('');
        }}
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: '#ffffff' }}>
          <Modal.Title>
            Abonnement kündigen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <p className="mb-3">
              Sind Sie sicher, dass Sie Ihr Abonnement kündigen möchten?
            </p>
            <div className="alert alert-info">
              <Icon name="info" size="sm" className="me-2" />
              <strong>Hinweis:</strong> Ihr Zugang bleibt bis zum Ende der 6-monatigen Mindestlaufzeit aktiv.
              Sie können bis dahin alle Funktionen weiterhin nutzen.
            </div>
            <p className="text-muted mb-0">
              Sie können die Kündigung jederzeit vor Ablauf des Zeitraums rückgängig machen.
            </p>
          </div>

          {cancelError && (
            <Alert variant="danger" className="mb-3">
              {cancelError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => {
              setShowCancelModal(false);
              setCancelError('');
            }}
            disabled={cancelLoading}
            style={{ border: '1px solid #dee2e6' }}
          >
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Wird gekündigt...
              </>
            ) : (
              'Ja, kündigen'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancellation Success Modal */}
      <Modal
        show={showCancelSuccessModal}
        onHide={() => setShowCancelSuccessModal(false)}
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow"
        style={{
          '--bs-modal-border-radius': '16px'
        }}
        centered
      >
        <Modal.Header style={{ backgroundColor: '#ffffff', borderBottom: 'none' }}>
          <Modal.Title className="w-100 text-center">
            <Icon name="check" size="lg" className="text-success me-2" />
            Kündigung bestätigt
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="mb-0" style={{ fontSize: '1.1rem' }}>
            {cancelSuccessMessage}
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', justifyContent: 'center' }}>
          <Button
            variant="primary"
            onClick={() => setShowCancelSuccessModal(false)}
            style={{ minWidth: '120px' }}
          >
            Verstanden
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Settings;